/**
 * Footer banner for the standalone question layout.
 *
 * Full-width, full-bleed background at the bottom of standalone pages. Uses CSS
 * subgrid to align with the parent grid columns. Footer content (links, legal
 * text) is driven by deployment config and legal doc links; renders nothing
 * when the required config is absent.
 */

import { useTheme } from '@repo/theme';
import { useLegalDocLinks } from 'component/admin';
import { spacings } from 'component/constants';
import { LinkText } from 'component/text';
import React from 'react';
import { useSiloKey } from 'util/datastore';
import { type TLabel, t } from 'util/stdtypes';
import { getDeploymentConfig } from 'util/util';

type FooterData = {
    linkUrl: string;
    linkText: string;
    privacyNoticeLink: string;
    termsOfServiceLink: string;
    privacyNoticeLabel: TLabel;
    termsOfServiceLabel: TLabel;
};

export function useFooterData(): FooterData | null {
    const siloKey = useSiloKey();
    const { privacyNoticeLink, termsOfServiceLink } = useLegalDocLinks(siloKey);
    const { linkUrl, linkText, privacyNoticeLabel, termsOfServiceLabel } =
        getDeploymentConfig().siloConfig?.[siloKey]?.standalonePageConfig?.footer || {};
    const shouldRenderFooter = linkUrl && linkText;

    if (!shouldRenderFooter) {
        return null;
    }
    return {
        linkUrl,
        linkText,
        privacyNoticeLink,
        termsOfServiceLink,
        privacyNoticeLabel: privacyNoticeLabel
            ? { key: null, defaultText: privacyNoticeLabel }
            : t('legal.privacy_notice', 'Privacy Notice'),
        termsOfServiceLabel: termsOfServiceLabel
            ? { key: null, defaultText: termsOfServiceLabel }
            : t('legal.terms_of_service', 'Terms of Service'),
    };
}

export function StandaloneQuestionFooter({ children }: { children: React.ReactNode }) {
    const { colors } = useTheme();
    return (
        <div
            style={{
                gridColumn: '1 / -1',
                width: '100%',
                backgroundColor: colors.colorSurfacePrimary,
                borderTop: `1px solid ${colors.colorBorderPrimary}`,
            }}
        >
            {children}
        </div>
    );
}

export const StandaloneFooterContent = ({
    isDesktop,
    linkText,
    linkUrl,
    privacyNoticeLink,
    termsOfServiceLink,
    privacyNoticeLabel,
    termsOfServiceLabel,
}: { isDesktop: boolean } & FooterData) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: isDesktop ? 'row' : 'column',
                justifyContent: 'space-between',
                gap: isDesktop ? spacings.xl3 : spacings.lg,
                padding: spacings.lg,
            }}
        >
            <LinkText text={linkText} type='large' url={linkUrl} weight='medium' />
            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: isDesktop ? spacings.xl3 : spacings.lg }}>
                <LinkText label={privacyNoticeLabel} type='large' url={privacyNoticeLink} weight='medium' />
                <LinkText label={termsOfServiceLabel} type='large' url={termsOfServiceLink} weight='medium' />
            </div>
        </div>
    );
};
